<template>
  <div class="service-page">
    <!-- Carousel Hero Section -->
    <ServicesItsCarouselComp 
      :slides="champaranEliteSlides"
      background-image="/resource/portfolio-03.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="champaranEliteMissionVision"
    />

    <!-- Luxury Services Showcase -->
    <ServiceItsCardEliteComp
      :header-data="luxuryServicesHeaderData"
      :luxury-services="luxuryServices"
      button-text="Book Service"
    />

    <!-- Elite Experience Section -->
    <ServiceItsExperienceComp
      :header-data="eliteExperienceHeaderData"
      :elite-features="eliteFeatures"
      :luxury-showcase="luxuryShowcase"
      button-text="Experience Elite"
    />

    <!-- Exclusive Service Gallery -->
    <ServiceItsCardGalleryComp
      :header-data="luxuryGalleryHeaderData"
      :luxury-gallery="luxuryGallery"
    />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import ServicesItsCarouselComp from '@/components/ServicesItsCarouselComp.vue'
import ServiceItsCardEliteComp from '@/components/ServiceItsCardEliteComp.vue'
import ServiceItsExperienceComp from '@/components/ServiceItsExperienceComp.vue'
import ServiceItsCardGalleryComp from '@/components/ServiceItsCardGalleryComp.vue'

export default {
  name: 'ChamparanEliteTouchPage',
  components: {
    ServicesItsCarouselComp,
    ServiceItsCardEliteComp,
    ServiceItsExperienceComp,
    ServiceItsCardGalleryComp
  },
  setup() {
    const luxuryServices = ref([
      {
        icon: 'fas fa-crown',
        title: 'Luxury Detailing',
        description: 'Premium car detailing services using only the finest products and techniques for showroom-quality results.',
        features: ['Paint Correction', 'Ceramic Coating', 'Leather Treatment', 'Interior Deep Clean'],
        detailedDescription: 'Experience the ultimate in automotive luxury with our premium detailing services.',
        price: '$299'
      },
      {
        icon: 'fas fa-tachometer-alt',
        title: 'Performance Tuning',
        description: 'Advanced performance modifications and tuning for high-performance and luxury vehicles.',
        features: ['ECU Remapping', 'Performance Exhaust', 'Air Intake Systems', 'Turbo Upgrades'],
        detailedDescription: 'Unleash the full potential of your luxury vehicle with our expert performance tuning.',
        price: '$899'
      },
      {
        icon: 'fas fa-concierge-bell',
        title: 'Concierge Service',
        description: 'White-glove concierge service with pickup, delivery, and personalized attention to your vehicle.',
        features: ['Pickup & Delivery', 'Loaner Vehicle', 'Progress Updates', 'Personal Service Manager'],
        detailedDescription: 'Experience the ultimate convenience with our exclusive concierge service.',
        price: '$199'
      },
      {
        icon: 'fas fa-cog',
        title: 'Premium Parts',
        description: 'Genuine OEM and premium aftermarket parts for luxury and high-performance vehicles.',
        features: ['OEM Parts', 'Performance Parts', 'Custom Modifications', 'Warranty Coverage'],
        detailedDescription: 'Only the finest parts and components for your luxury vehicle.',
        price: 'Custom'
      },
      {
        icon: 'fas fa-user-tie',
        title: 'Expert Technicians',
        description: 'Master-certified technicians with specialized training in luxury vehicle systems and diagnostics.',
        features: ['Factory Training', 'Specialized Tools', 'Advanced Diagnostics', 'Quality Assurance'],
        detailedDescription: 'Our master technicians provide the highest level of expertise and care.',
        price: 'Included'
      },
      {
        icon: 'fas fa-champagne-glasses',
        title: 'Luxury Amenities',
        description: 'Premium amenities and services to enhance your service experience and comfort.',
        features: ['Executive Lounge', 'Refreshments', 'WiFi & Entertainment', 'Valet Parking'],
        detailedDescription: 'Relax in luxury while we care for your vehicle with premium amenities.',
        price: 'Included'
      }
    ])

    const eliteFeatures = ref([
      {
        icon: 'fas fa-star',
        title: 'Personalized Service',
        description: 'Every detail tailored to your specific needs and preferences.'
      },
      {
        icon: 'fas fa-shield-alt',
        title: 'Premium Warranty',
        description: 'Comprehensive warranty coverage on all services and parts.'
      },
      {
        icon: 'fas fa-clock',
        title: 'Priority Scheduling',
        description: 'Exclusive access to priority scheduling and expedited service.'
      }
    ])

    const luxuryShowcase = ref([
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Luxury Detailing',
        description: 'Showroom-quality results'
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Performance Tuning',
        description: 'Maximum power output'
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Concierge Service',
        description: 'White-glove treatment'
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'Premium Parts',
        description: 'Genuine OEM quality'
      }
    ])

    const luxuryGallery = ref([
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Luxury Detailing Transformation',
        description: 'Complete paint correction and ceramic coating for showroom finish.',
        stats: [
          { value: '100%', label: 'Paint Correction' },
          { value: '5 Years', label: 'Ceramic Coating' },
          { value: 'Showroom', label: 'Quality Finish' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Performance Enhancement',
        description: 'Advanced ECU tuning and performance modifications for maximum power.',
        stats: [
          { value: '+40%', label: 'Power Increase' },
          { value: '500HP', label: 'Peak Power' },
          { value: '3.2s', label: '0-60 Time' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Interior Restoration',
        description: 'Complete interior restoration and luxury upgrades.',
        stats: [
          { value: '100%', label: 'Leather Treatment' },
          { value: 'Premium', label: 'Materials' },
          { value: '5 Years', label: 'Warranty' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'Custom Modifications',
        description: 'Bespoke modifications tailored to your exact specifications.',
        stats: [
          { value: 'Custom', label: 'Design' },
          { value: 'OEM', label: 'Quality' },
          { value: 'Lifetime', label: 'Support' }
        ]
      }
    ])

    // Champaran Elite Touch slides data
    const champaranEliteSlides = ref([
      {
        subtitle: 'Premium Services',
        titleLines: [
          { text: 'Champaran ', highlight: false },
          { text: 'Elite ', highlight: 'Touch' }
        ],
        description: 'Exclusive premium automotive services for discerning customers. Experience luxury car care with personalized attention, premium parts, and white-glove service.',
        buttonText: 'Experience Luxury',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Luxury Care',
        titleLines: [
          { text: 'Premium ', highlight: 'Experience' }
        ],
        description: 'Indulge in our premium automotive services designed for luxury vehicles. From exotic car maintenance to concierge service, we deliver excellence.',
        buttonText: 'Learn More',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Exclusive Service',
        titleLines: [
          { text: 'White-Glove ', highlight: 'Service' }
        ],
        description: 'Our elite service includes personal consultation, premium parts, specialized tools, and dedicated technicians for your luxury vehicle needs.',
        buttonText: 'Book Service',
        buttonLink: '/contact'
      }
    ]);

    // Champaran Elite Touch mission/vision data
    const champaranEliteMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the premier luxury automotive service provider offering exclusive, personalized care for high-end vehicles with unmatched attention to detail.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To deliver exceptional luxury automotive services that exceed expectations through premium parts, expert craftsmanship, and personalized white-glove service.'
      }
    });

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()

      // Import and initialize Owl Carousel
      try {
        await import('owl.carousel')
        
        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$('.banner-carousel').owlCarousel({
              loop: true,
              margin: 0,
              nav: true,
              active: true,
              smartSpeed: 1000,
              autoplay: 6000,
              navText: ['<span class="icon-05"></span>', '<span class="icon-06"></span>'],
              responsive: {
                0: {
                  items: 1
                },
                600: {
                  items: 1
                },
                768: {
                  items: 1
                },
                992: {
                  items: 1
                },
                1200: {
                  items: 1
                }
              }
            })
          }
        }, 100)
      } catch (error) {
        console.error('Error loading Owl Carousel:', error)
      }
    })

    // Header data for components
    const luxuryServicesHeaderData = {
      icon: "fas fa-crown",
      tag: "ELITE SERVICES",
      title: "Champaran Elite Touch",
      description: "Exclusive premium automotive services for the most discerning customers"
    }

    const eliteExperienceHeaderData = {
      title: "The Elite Experience",
      description: "Step into a world of unparalleled luxury and sophistication. Our elite service experience is designed to exceed the expectations of the most discerning automotive enthusiasts."
    }

    const luxuryGalleryHeaderData = {
      icon: "fas fa-gem",
      tag: "LUXURY GALLERY",
      title: "Exclusive Service Gallery",
      description: "Witness the transformation of luxury vehicles through our premium services"
    }

    return {
      luxuryServices,
      eliteFeatures,
      luxuryShowcase,
      luxuryGallery,
      champaranEliteSlides,
      champaranEliteMissionVision,
      luxuryServicesHeaderData,
      eliteExperienceHeaderData,
      luxuryGalleryHeaderData
    }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Custom button styling for carousel - More specific to avoid conflicts */
.service-page .banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #FB0E76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.service-page .banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.10);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
}

/* Reduce banner height - More specific to avoid conflicts */
.service-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

.service-page .banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments - More specific to avoid conflicts */
@media (max-width: 768px) {
  .service-page .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }
  
  .service-page .mission__block {
    padding: 30px 25px !important;
  }
  
  .service-page .mission__block h3 {
    font-size: 18px !important;
  }
  
  .service-page .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Global Styles */
body {
  font-family: 'Open Sans', sans-serif;
  line-height: 1.6;
  color: #333;
}

.see__pad {
  padding: 100px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Pattern Layers */
.pattern-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.pattern-1 {
  top: 10%;
  right: 5%;
  width: 100px;
  height: 100px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-2 {
  bottom: 20%;
  left: 8%;
  width: 80px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-3 {
  top: 30%;
  left: 10%;
  width: 120px;
  height: 120px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-4 {
  top: 15%;
  right: 15%;
  width: 90px;
  height: 90px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-5 {
  bottom: 25%;
  right: 20%;
  width: 70px;
  height: 70px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-6 {
  top: 40%;
  left: 5%;
  width: 110px;
  height: 110px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.09;
}

.pattern-7 {
  bottom: 15%;
  left: 12%;
  width: 85px;
  height: 85px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-8 {
  top: 35%;
  right: 8%;
  width: 95px;
  height: 95px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

/* Intro Section */
.service-intro {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.service-intro-content {
  position: relative;
  z-index: 2;
}

.service-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.service-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.service-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Services Offered Section */
.services-offered {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.services-header {
  position: relative;
  z-index: 2;
  margin-bottom: 60px;
}

.services-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.services-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.services-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

.service-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 15px;
  padding: 40px 30px;
  height: 100%;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.service-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(246, 236, 79, 0.2);
  border-color: #F6EC4F;
}

.service-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.block-shape-1 {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(246, 236, 79, 0.1), rgba(246, 236, 79, 0.05));
  border-radius: 50%;
  opacity: 0.3;
}

.block-shape-2 {
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(246, 236, 79, 0.08), rgba(246, 236, 79, 0.03));
  border-radius: 50%;
  opacity: 0.4;
}

.service-content {
  position: relative;
  z-index: 2;
}

.service-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
}

.service-icon i {
  font-size: 24px;
  color: #00061A;
}

.service-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.service-content p {
  font-size: 16px;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.6;
}

.service-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-content ul li {
  color: #fff;
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.service-content ul li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #F6EC4F;
  font-weight: bold;
}

/* Call to Action Section */
.service-cta {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 2;
}

.cta-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.cta-description p {
  font-size: 18px;
  color: #fff;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.8;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.primary-btn {
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  color: #00061A;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.3);
}

.secondary-btn {
  background: transparent;
  color: #F6EC4F;
  border-color: #F6EC4F;
}

.secondary-btn:hover {
  background: #F6EC4F;
  color: #00061A;
  transform: translateY(-2px);
}

/* Luxury Services Showcase Section - Now handled by ServiceItsCardEliteComp component */





/* Luxury Gallery Section */
.luxury-gallery-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.gallery-luxury-header {
  text-align: center;
  margin-bottom: 60px;
}

.gallery-luxury-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #1a1a1a;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
}

.gallery-luxury-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.gallery-luxury-subtitle {
  font-size: 18px;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.luxury-gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.gallery-luxury-item {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.gallery-luxury-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.gallery-luxury-image {
  position: relative;
  overflow: hidden;
}

.gallery-luxury-image img {
  width: 100%;
  height: 250px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-luxury-item:hover .gallery-luxury-image img {
  transform: scale(1.1);
}

.gallery-luxury-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.9), rgba(255, 237, 78, 0.9));
  color: #1a1a1a;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.gallery-luxury-item:hover .gallery-luxury-overlay {
  opacity: 1;
}

.overlay-luxury-content {
  text-align: center;
}

.overlay-luxury-content h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  font-family: 'Archivo', sans-serif;
  color: #1a1a1a;
  text-align: center;
}

.luxury-stats {
  display: flex;
  justify-content: center;
  align-items: center;
}

.luxury-stat {
  text-align: center;
}

.luxury-stat-value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 3px;
}

.luxury-stat-label {
  font-size: 11px;
  color: rgba(26, 26, 26, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Elite Experience Section - Now handled by ServiceItsExperienceComp component */


.elite-experience-section .container {
  position: relative;
  z-index: 2;
}

.elite-content {
  padding-right: 40px;
}

.elite-title {
  font-size: 48px;
  font-weight: 700;
  color: #ffd700;
  margin-bottom: 25px;
  font-family: 'Archivo', sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.elite-description {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
  line-height: 1.6;
}

.elite-features {
  margin-bottom: 40px;
}

.elite-feature {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 215, 0, 0.2);
  transition: all 0.3s ease;
}

.elite-feature:hover {
  background: rgba(255, 215, 0, 0.1);
  border-color: rgba(255, 215, 0, 0.4);
  transform: translateX(10px);
}

.feature-luxury-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.feature-luxury-icon i {
  font-size: 24px;
  color: #1a1a1a;
}

.feature-luxury-text h4 {
  font-size: 20px;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 8px;
  font-family: 'Archivo', sans-serif;
}

.feature-luxury-text p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

.elite-btn {
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #1a1a1a;
  border: none;
  padding: 15px 35px;
  border-radius: 30px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
}

.elite-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(255, 215, 0, 0.4);
  background: linear-gradient(135deg, #ffed4e, #ffd700);
}

.elite-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 500px;
}

.luxury-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
  max-width: 400px;
}

.showcase-item {
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.showcase-item:hover {
  transform: scale(1.05);
  box-shadow: 0 15px 40px rgba(255, 215, 0, 0.2);
}

.showcase-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.showcase-item:hover img {
  transform: scale(1.1);
}

.showcase-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.9), rgba(255, 237, 78, 0.9));
  color: #1a1a1a;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: all 0.3s ease;
  text-align: center;
}

.showcase-item:hover .showcase-overlay {
  opacity: 1;
}

.showcase-overlay h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  font-family: 'Archivo', sans-serif;
}

.showcase-overlay p {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(26, 26, 26, 0.8);
}

/* Responsive Design */
@media (max-width: 767px) {
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 36px;
    line-height: 46px;
  }
  
  .service-section-label h4,
  .services-section-label h4 {
    letter-spacing: 8px;
    font-size: 16px;
  }
  
  .service-item {
    padding: 30px 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}

@media (max-width: 399px) {
  .see__pad {
    padding: 60px 0;
  }
  
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 28px;
    line-height: 38px;
  }
}
</style>